import { FC, forwardRef } from "react";

import type { Breakpoint } from "@mui/material";
import MuiStack, { StackProps as MuiStackProps } from "@mui/material/Stack";
import styled from "@mui/material/styles/styled";

import type { PartialBy } from "@readai/common/types";

type StackVariant = "center" | "left" | "right" | "apart";

export interface StackProps extends MuiStackProps {
  variant?: StackVariant;
  showAbove?: Breakpoint;
  showBelow?: Breakpoint;
}

type StackAlignment = PartialBy<MuiStackProps, "justifyContent" | "alignItems">;

const centerProps: StackAlignment = {
  justifyContent: "center",
  alignItems: "center",
};

const leftProps: StackAlignment = {
  justifyContent: "start",
  alignItems: "start",
};

const leftRowProps: StackAlignment = {
  justifyContent: "start",
  alignItems: "center",
};

const rightProps: StackAlignment = {
  justifyContent: "end",
  alignItems: "end",
};

const rightRowProps: StackAlignment = {
  justifyContent: "end",
  alignItems: "center",
};

const apartProps: StackAlignment = {
  justifyContent: "space-between",
  alignItems: "center",
};

const COLUMN_PROPS_BY_VARIANT: { [key in StackVariant]: StackAlignment } = {
  center: centerProps,
  left: leftProps,
  right: rightProps,
  apart: apartProps,
};

const ROW_PROPS_BY_VARIANT: { [key in StackVariant]: StackAlignment } = {
  center: centerProps,
  left: leftRowProps,
  right: rightRowProps,
  apart: apartProps,
};

const BreakpointStack = styled<FC<StackProps>>(MuiStack, {
  shouldForwardProp: (prop) => prop !== "showAbove" && prop !== "showBelow",
})(({ theme, showAbove, showBelow }) => ({
  ...(showBelow && {
    [theme.breakpoints.up(showBelow)]: {
      display: "none",
    },
  }),
  ...(showAbove && {
    [theme.breakpoints.down(showAbove)]: {
      display: "none",
    },
  }),
}));

const Row = forwardRef<HTMLDivElement | null, StackProps>(
  (props: StackProps, ref) => <Stack direction="row" {...props} ref={ref} />,
);
Row.displayName = "Row";

const Column = forwardRef<HTMLDivElement | null, StackProps>(
  (props: StackProps, ref) => <Stack direction="column" {...props} ref={ref} />,
);
Column.displayName = "Column";

/**
 *  Base Mui Stack component with added variants: `center`, `left`, `right`, `apart`
 */
const Stack = forwardRef<HTMLDivElement | null, StackProps>(
  ({ variant, ...props }: StackProps, ref) => {
    const alignmentProps = variant
      ? (props.direction === "row"
          ? ROW_PROPS_BY_VARIANT
          : COLUMN_PROPS_BY_VARIANT)[variant]
      : {};

    return (
      <BreakpointStack useFlexGap {...alignmentProps} {...props} ref={ref} />
    );
  },
);
Stack.displayName = "Stack";

export { Column, Row };
export default Stack;
